
import {
  Employee,
  LeaveRequestStatusEnum,
  OpenShiftResponseStatusEnum,
  Shift,
} from '@/../api/v1';
import { openShiftResponseApi } from '@/api';
import ActionDialog from '@/components/dialog/ActionDialog.vue';
import {
  absenceEndDate,
  absenceStartDate,
} from '@/lib/absence/absenceFunctions';
import { PlainDate } from '@/lib/date-time/PlainDate';
import { Icon } from '@/lib/enum/Icon';
import { leaveDateFilter } from '@/lib/leave/leaveFilters';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { showSuccess } from '@/plugins/ToastNotifications';
import store from '@/store';
import { PropType } from 'vue';
import OpenShiftResponseDetails from './components/OpenShiftResponseDetails.vue';

export default {
  name: 'CreateOpenShiftResponse',
  components: {
    OpenShiftResponseDetails,
    ActionDialog,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    shift: {
      type: Object as PropType<Shift>,
      required: true,
    },
  },
  data() {
    const {
      loggedInEmployee,
      timezone,
    }: {
      loggedInEmployee: Employee;
      timezone: string;
    } = store.getters;
    const { startsAt, endsAt } = this.shift;
    // plain date for start and end days would be different for night shifts
    const plainStart = PlainDate.fromDate(startsAt, timezone);
    const plainEnd = PlainDate.fromDate(endsAt, timezone);

    return {
      Icon,
      detailsOpen: false,
      employeeUnavailability: realtimeQuery(Entity.Unavailability)
        .where('employeeId', 'eq', loggedInEmployee.id)
        .whereDatesTimesOverlap('startsAt', 'endsAt', startsAt, endsAt)
        .fetch(),
      employeeLeave: realtimeQuery(Entity.LeaveRequest)
        .where('employeeId', 'eq', loggedInEmployee.id)
        .whereIn('status', [
          LeaveRequestStatusEnum.Approved,
          LeaveRequestStatusEnum.PendingApproval,
          LeaveRequestStatusEnum.PendingCancellation,
        ])
        .whereDatesOverlap('startDate', 'endDate', plainStart, plainEnd)
        .fetch(),
      employeeAbsences: realtimeQuery(Entity.Absence)
        .where('employeeId', 'eq', loggedInEmployee.id)
        .whereDatesOverlap('startedOn', 'endedOn', plainStart, plainEnd)
        .fetch(),
      submitting: false,
    };
  },
  computed: {
    timezone: (): string => store.getters.timezone,

    open: {
      get(): boolean {
        return this.value;
      },
      set(val: boolean) {
        this.$emit('input', val);
      },
    },

    conflictMessage(): { header: string; body: string } | null {
      if (this.employeeUnavailability.data.length) {
        return {
          header: this.$tc('info.heading.unavailabilityConflict'),
          body: this.$tc('info.thisShiftConflictsWithUnavailability'),
        };
      }
      if (
        this.employeeLeave.data.filter(
          leaveDateFilter(
            this.shift.startsAt,
            this.shift.endsAt,
            this.timezone,
          ),
        ).length
      ) {
        return {
          header: this.$tc('info.heading.leaveConflict'),
          body: this.$tc('info.thisShiftConflictsWithLeave'),
        };
      }
      if (
        this.employeeAbsences.data.filter(
          (a) =>
            absenceStartDate(a, this.timezone) < this.shift.endsAt &&
            absenceEndDate(a, this.timezone) > this.shift.startsAt,
        ).length
      ) {
        return {
          header: this.$tc('info.heading.absenceConflict'),
          body: this.$tc('info.thisShiftConflictsWithAbsence'),
        };
      }
      return null;
    },
  },
  methods: {
    async respond() {
      this.submitting = true;
      try {
        const { data: response } =
          await openShiftResponseApi.createOpenShiftResponse({
            createOpenShiftResponseData: {
              shiftId: this.shift.id,
            },
          });
        if (response.status === OpenShiftResponseStatusEnum.Accepted) {
          showSuccess(this.$t('successMessage.openShiftResponseAccepted'));
        } else {
          showSuccess(this.$t('successMessage.openShiftResponsePending'));
        }
        this.open = false;
      } finally {
        this.submitting = false;
      }
    },
  },
};
